import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import { calcClientDerivatives } from '@shared';
import { coverageKeyFor } from '@shared/calculations/funding/projection/projectionToLegacy';
import type { AppState } from '@client/model/store';
import { selectMaybeClientPartner } from '@client/model/selectors';
import { carePhaseLabel, fundingPolicyTypeLabel } from '../glossary/enumLabels';

const selectClientDataOrNull = (state: AppState): Client | null => state.client.data;

// Difference thresholds, mirroring the shadow parity harness
// (`projectionParityDiff.ts` `pushDelta`): a delta is noise unless it clears
// both an absolute floor AND a relative tolerance. We split the absolute floor
// by value kind — dollar fields tolerate $1, fraction fields (coverage / ROI /
// CAGR / IRR, stored as 0..1) tolerate 0.01 percentage points — so the harness's
// flat $1 floor doesn't silently suppress every coverage/ROI difference.
const REL_TOLERANCE = 0.01;
const CURRENCY_ABS_FLOOR = 1;
const FRACTION_ABS_FLOOR = 0.0001;

export type DiffFormat = 'currency' | 'percent';

export interface DiffRow {
  field: string;
  label: string;
  format: DiffFormat;
  legacy: number | undefined;
  next: number | undefined;
  relDiff: number;
  differs: boolean;
}

export interface PhaseDiffGroup {
  label: string;
  rows: DiffRow[];
}

export interface PolicyDiffGroup {
  fundingSourceId: string;
  label: string;
  scalarRows: DiffRow[];
  phaseGroups: PhaseDiffGroup[];
}

export interface EngineDiff {
  policies: PolicyDiffGroup[];
  combinedRows: DiffRow[];
  anyDiffer: boolean;
}

function makeRow(
  field: string,
  label: string,
  format: DiffFormat,
  legacy: number | null | undefined,
  next: number | null | undefined,
): DiffRow {
  const l = legacy ?? 0;
  const n = next ?? 0;
  const abs = Math.abs(l - n);
  const floor = format === 'currency' ? CURRENCY_ABS_FLOOR : FRACTION_ABS_FLOOR;
  let relDiff = 0;
  let differs = false;
  if (abs > floor) {
    const denom = Math.max(Math.abs(l), Math.abs(n), floor);
    relDiff = abs / denom;
    differs = relDiff > REL_TOLERANCE;
  }
  return {
    field,
    label,
    format,
    legacy: legacy ?? undefined,
    next: next ?? undefined,
    relDiff,
    differs,
  };
}

const PHASE_FIELDS: { field: keyof SinglePhaseCalculations; label: string; format: DiffFormat }[] = [
  { field: 'phaseCostCoverage', label: 'Cost coverage', format: 'currency' },
  { field: 'inflatedDailyBenefitAmount', label: 'Inflated daily benefit', format: 'currency' },
  { field: 'inflatedMaxBenefitAmount', label: 'Inflated max benefit', format: 'currency' },
];

function policyScalarRows(
  legacyPolicy: PolicyFundingSource | undefined,
  nextPolicy: PolicyFundingSource | undefined,
  clientHasStartedLtc: boolean,
): DiffRow[] {
  const coverageKey = coverageKeyFor(clientHasStartedLtc);
  return [
    makeRow(
      coverageKey,
      'Cost coverage',
      'currency',
      legacyPolicy?.[coverageKey] as number | undefined,
      nextPolicy?.[coverageKey] as number | undefined,
    ),
    makeRow('policyTotalValue', 'Total value', 'currency', legacyPolicy?.policyTotalValue, nextPolicy?.policyTotalValue),
    makeRow('policyNonLtcPayout', 'Non-LTC payout', 'currency', legacyPolicy?.policyNonLtcPayout, nextPolicy?.policyNonLtcPayout),
    makeRow('policyRemainingBenefitAmount', 'Remaining benefit', 'currency', legacyPolicy?.policyRemainingBenefitAmount, nextPolicy?.policyRemainingBenefitAmount),
    makeRow('policyProjectedReturnOnInvestment', 'ROI', 'percent', legacyPolicy?.policyProjectedReturnOnInvestment, nextPolicy?.policyProjectedReturnOnInvestment),
    makeRow('policyProjectedCompoundAnnualGrowthRate', 'CAGR', 'percent', legacyPolicy?.policyProjectedCompoundAnnualGrowthRate, nextPolicy?.policyProjectedCompoundAnnualGrowthRate),
    makeRow('policyProjectedInternalRateOfReturn', 'IRR', 'percent', legacyPolicy?.policyProjectedInternalRateOfReturn, nextPolicy?.policyProjectedInternalRateOfReturn),
  ];
}

function phaseDiffGroups(
  legacyPhases: SinglePhaseCalculations[] | undefined,
  nextPhases: SinglePhaseCalculations[] | undefined,
): PhaseDiffGroup[] {
  const count = Math.max(legacyPhases?.length ?? 0, nextPhases?.length ?? 0);
  const groups: PhaseDiffGroup[] = [];
  for (let i = 0; i < count; i += 1) {
    const legacyPhase = legacyPhases?.[i];
    const nextPhase = nextPhases?.[i];
    if (!legacyPhase && !nextPhase) continue;
    const phase = legacyPhase?.phase ?? nextPhase?.phase;
    const rows = PHASE_FIELDS.map(({ field, label, format }) =>
      makeRow(
        `phase[${i}].${field}`,
        label,
        format,
        legacyPhase?.[field] as number | null | undefined,
        nextPhase?.[field] as number | null | undefined,
      ),
    );
    groups.push({ label: carePhaseLabel(phase), rows });
  }
  return groups;
}

function combinedRows(legacy: Client, next: Client): DiffRow[] {
  const l = legacy.fundingSources;
  const n = next.fundingSources;
  return [
    makeRow('combinedProjectedCostCoverage', 'Cost coverage', 'currency', l.combinedProjectedCostCoverage, n.combinedProjectedCostCoverage),
    makeRow('combinedTotalValue', 'Total value', 'currency', l.combinedTotalValue, n.combinedTotalValue),
    makeRow('combinedNonLtcPayout', 'Non-LTC payout', 'currency', l.combinedNonLtcPayout, n.combinedNonLtcPayout),
    makeRow('combinedProjectedReturnOnInvestment', 'ROI', 'percent', l.combinedProjectedReturnOnInvestment, n.combinedProjectedReturnOnInvestment),
    makeRow('combinedProjectedCompoundAnnualGrowthRate', 'CAGR', 'percent', l.combinedProjectedCompoundAnnualGrowthRate, n.combinedProjectedCompoundAnnualGrowthRate),
    makeRow('combinedProjectedInternalRateOfReturn', 'IRR', 'percent', l.combinedProjectedInternalRateOfReturn, n.combinedProjectedInternalRateOfReturn),
  ];
}

/**
 * Runs the currently-loaded client through both calc engines (legacy and the
 * new claim-projection engine) and returns a structured field-by-field diff,
 * grouped per LTC policy plus the combined totals. The engine choice is forced
 * via `ClientDerivativeOptions.useClaimProjectionEngine`, which overrides the
 * persisted client setting, so neither run mutates the stored client. Unported
 * products fall back to legacy in both runs, so their rows never differ.
 *
 * Both engines always run on the whole client (required — the calc isn't
 * per-policy), but when `selectedFundingSourceId` is given the result is scoped
 * to just that policy. The cross-policy "Combined" totals are omitted in that
 * case, since they aggregate every product rather than the selected one.
 */
export function useEngineDiff(selectedFundingSourceId?: string): EngineDiff | null {
  const client = useSelector(selectClientDataOrNull);
  const partner = useSelector(selectMaybeClientPartner);

  // Run both engines once per client/partner change. This is the expensive
  // part (full derivative pipeline ×2); it does NOT depend on which funding
  // source is selected, so picking a different policy must not re-run it.
  const engines = useMemo(() => {
    if (!client) return null;
    const legacy = calcClientDerivatives(
      cloneDeep(client),
      partner ? cloneDeep(partner) : null,
      { useClaimProjectionEngine: false },
    ) as Client;
    const next = calcClientDerivatives(
      cloneDeep(client),
      partner ? cloneDeep(partner) : null,
      { useClaimProjectionEngine: true },
    ) as Client;
    return { legacy, next, clientHasStartedLtc: client.intakeSurvey.clientHasStartedLtc };
  }, [client, partner]);

  // Build the diff rows. Cheap; re-runs when the selected funding source
  // changes without recomputing either engine.
  return useMemo(() => {
    if (!engines) return null;
    const { legacy, next, clientHasStartedLtc } = engines;

    const legacyPolicies = legacy.multipleFundingSources?.ltcPolicy ?? {};
    const nextPolicies = next.multipleFundingSources?.ltcPolicy ?? {};
    const fundingSourceIds = Object.keys(legacyPolicies).filter(
      id => !selectedFundingSourceId || id === selectedFundingSourceId,
    );

    const policies: PolicyDiffGroup[] = fundingSourceIds.map(id => {
      const legacyPolicy = legacyPolicies[id];
      const nextPolicy = nextPolicies[id];
      const customName = legacyPolicy?.policyCustomName ?? undefined;
      const typeLabel = fundingPolicyTypeLabel(legacyPolicy?.policyType as unknown as number);
      return {
        fundingSourceId: id,
        label: customName ?? typeLabel,
        scalarRows: policyScalarRows(legacyPolicy, nextPolicy, clientHasStartedLtc),
        phaseGroups: phaseDiffGroups(
          legacy.policyPhaseCalculations?.[id],
          next.policyPhaseCalculations?.[id],
        ),
      };
    });

    // Combined is a cross-policy rollup, so only meaningful when not scoped to
    // a single product.
    const combined = selectedFundingSourceId ? [] : combinedRows(legacy, next);

    const anyDiffer =
      combined.some(r => r.differs) ||
      policies.some(
        p =>
          p.scalarRows.some(r => r.differs) ||
          p.phaseGroups.some(g => g.rows.some(r => r.differs)),
      );

    return { policies, combinedRows: combined, anyDiffer };
  }, [engines, selectedFundingSourceId]);
}
